import { cx } from '@emotion/css'
import { alpha, IconButton as MaterialIconButton } from '@mui/material'
import React, { CSSProperties } from 'react'
import {
  CssStyles,
  IconName,
  NamedIconButtonColor,
  NamedSize,
} from '../../types'
import {
  getClassName,
  isNamedIconButtonColor,
  parseClassNames,
} from '../../utils'
import { RobotenIcon } from '../RobotenIcon'
import { Theme } from '@mui/material/styles'

export interface IIconButton {
  /** Screen reader text for the visually impaired and bots.
   *
   */
  alt?: string

  /** The icon to display.
   *
   * @default "star"
   */
  icon?: IconName

  /**
   *  The component used for the root node.
   */
  component?: string

  /** If true, the base button will be disabled.
   *
   * @default false
   */
  disabled?: boolean

  /** The size of the component.
   *
   * @default medium
   */
  size?: NamedSize | number

  /** The color of the component.
   *
   * @default "primary"
   */
  color?: NamedIconButtonColor | string

  /**
   * Type of the button, submit can be used within a Form to trigget submit.
   * @default "button"
   */
  type: 'button' | 'submit'

  /** Provide an optional function to be called when a mouse button is pressed */
  onMouseDown?: (evt: React.MouseEvent<HTMLButtonElement>) => unknown

  /** Provide an optional function to be called when a mouse button is released */
  onMouseUp?: (evt: React.MouseEvent<HTMLButtonElement>) => unknown

  /** Provide an optional function to be called when the button is clicked */
  onClick?: (evt: React.MouseEvent<HTMLButtonElement>) => void

  /** Provide an optional function to be called when the context menu is opened */
  onContextMenu?: (evt: React.MouseEvent<HTMLButtonElement>) => void

  /** *Deprecated* Raw CSS styles
   * @deprecated
   */
  styles?: CssStyles<'root' | 'icon'>

  style?: CSSProperties | string
  iconStyle?: CSSProperties | string
}

const getButtonBackgroundColor = ({
  theme,
  color,
}: {
  theme: Theme
  color: IIconButton['color']
}): string => {
  const { palette } = theme
  switch (color) {
    case 'primary':
      return palette.primary.main
    case 'gray':
      return palette.black.main
    case 'error':
      return palette.error.dark
    case 'warning':
      return palette.warning.dark
    case 'success':
      return palette.success.dark
    default:
      return palette.primary.main
  }
}

export const IconButton = React.forwardRef<HTMLButtonElement, IIconButton>(
  (
    {
      color = 'primary',
      size = 'medium',
      icon = 'star',
      alt,
      styles,
      style,
      iconStyle,
      ...rest
    },
    ref
  ) => {
    const classNames = styles && parseClassNames(styles)

    return (
      <MaterialIconButton
        ref={ref}
        color={isNamedIconButtonColor(color) ? color : undefined}
        classes={{ root: cx(classNames?.root, getClassName(style)) }}
        disableRipple
        sx={(theme) => {
          const bgColor = getButtonBackgroundColor({ theme, color })
          return {
            width: theme.spacing(4.5),
            height: theme.spacing(4.5),
            padding: theme.spacing(0.75),
            borderRadius: theme.spacing(0.5),
            color: isNamedIconButtonColor(color) ? undefined : color,

            '&:hover': {
              backgroundColor: alpha(bgColor, 0.04),
            },
            '&:active, &:focus': {
              backgroundColor: alpha(bgColor, 0.12),
            },
          }
        }}
        {...rest}
      >
        {icon ? (
          <RobotenIcon
            size={size}
            iconName={icon}
            alt={alt}
            styles={styles?.icon ? { root: styles.icon } : undefined}
            style={iconStyle}
          />
        ) : null}
      </MaterialIconButton>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  IconButton.displayName = 'IconButton'
}
